import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  LinearProgress,
  Paper,
  Button,
  useMediaQuery,
  alpha,
  TextField,
  IconButton,
  Collapse,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const AssessmentDisplay = ({ scores, styles, onSave }) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [editedScores, setEditedScores] = useState(scores);
  const [expandedMotivations, setExpandedMotivations] = useState({});
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const gradeInputRef = useRef(null);

  useEffect(() => {
    setEditedScores(scores);
    setIsEditing(false); // Avsluta redigeringsläge när nya scores kommer
  }, [scores]);

  const getGradeColor = (grade) => {
    const gradeColors = {
      A: "#2E7D32",
      B: "#1976D2",
      C: "#FFA000",
      D: "#ED6C02",
      E: "#D32F2F",
      F: "#757575",
    };
    return gradeColors[grade] || "#757575";
  };

  const handleScoreChange = (criteriaKey, field, value) => {
    setEditedScores((prev) => ({
      ...prev,
      criteriaScores: {
        ...prev.criteriaScores,
        [criteriaKey]: {
          ...prev.criteriaScores[criteriaKey],
          [field]:
            field === "score"
              ? Math.min(5, Math.max(0, parseFloat(value) || 0))
              : value,
        },
      },
    }));
  };

  const handleOverallChange = (field, value) => {
    setEditedScores((prev) => ({
      ...prev,
      overallScore: {
        ...prev.overallScore,
        [field]: field === "grade" ? value.toUpperCase() : value,
      },
    }));
  };

  const toggleMotivation = (key) => {
    setExpandedMotivations((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSave = () => {
    onSave(editedScores);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedScores(scores);
    setIsEditing(false);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        ...styles.freshBox,
        p: 3,
        pb: 1.5,
        borderRadius: 2,
        maxWidth: "100%",
        margin: "0 auto",
        mb: 6,
        position: "relative",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          bottom: -50,
          right: 0,
          opacity: isMobile ? 1 : 0,
          transition: "opacity 0.2s",
          ".MuiPaper-root:hover &": {
            opacity: 1,
          },
        }}
      >
        {!isEditing ? (
          <>
            <Button
              startIcon={<EditIcon />}
              onClick={() => setIsEditing(true)}
              variant="print"
              sx={{ ...styles.tightButton }}
            >
              Redigera betyg
            </Button>
          </>
        ) : (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              startIcon={<SaveIcon />}
              onClick={handleSave}
              variant="contained"
              variant="purp"
              sx={{ ...styles.tightButton }}
            >
              Spara
            </Button>
            <Button
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              variant="print"
              sx={{ ...styles.tightButton }}
            >
              Avbryt
            </Button>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 4,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Box
          onClick={() => {
            if (!isEditing) {
              setIsEditing(true);
              // Focus the grade input after state update
              setTimeout(() => {
                if (gradeInputRef.current) {
                  const input = gradeInputRef.current.querySelector("input");
                  if (input) {
                    input.focus();
                    input.select();
                  }
                }
              }, 0);
            }
          }}
          sx={{
            bgcolor: getGradeColor(editedScores.overallScore.grade),
            color: "white",
            width: { xs: "100%", md: 200 },
            height: { xs: 170, md: 235 },
            borderRadius: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            flexShrink: 0,
            alignSelf: "flex-start",
            cursor: isEditing ? "default" : "pointer",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              transform: isEditing ? "none" : "scale(1.02)",
            },
          }}
        >
          {isEditing ? (
            <TextField
              ref={gradeInputRef}
              value={editedScores.overallScore.grade}
              onChange={(e) => handleOverallChange("grade", e.target.value)}
              variant="outlined"
              size="small"
              inputProps={{
                style: {
                  color: "white",
                  textAlign: "center",
                  fontSize: "2.5rem",
                  fontWeight: "bold",
                },
                maxLength: 1,
              }}
              sx={{
                width: "80px",
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "white",
                  },
                  "&:hover fieldset": {
                    borderColor: "white",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "white",
                  },
                },
              }}
            />
          ) : (
            <Typography variant="h2" sx={{ fontWeight: "bold" }}>
              {editedScores.overallScore.grade}
            </Typography>
          )}
          <Typography variant="subtitle1" sx={{ mt: 1, fontSize: "0.9rem" }}>
            Övergripande betyg
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            minHeight: 100,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: { xs: 2, md: 0 },
              width: "100%",
            }}
          >
            {Object.entries(editedScores.criteriaScores).map(
              ([criteriaKey, criteria]) => (
                <Box key={criteriaKey}>
                  {/* Criteria Name */}
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 500,
                      fontSize: "0.95rem",
                      color: "text.secondary",
                      mb: { xs: 0, md: 0 },
                      display: { xs: "block", md: "none" },
                    }}
                  >
                    {criteriaKey}
                  </Typography>

                  {/* Main Grid Layout - Now Clickable */}
                  <Box
                    onClick={() => toggleMotivation(criteriaKey)}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr 60px 50px 30px", // Mobile: progress bar with score, grade, and expand button
                        md: "280px 1fr 60px 50px 30px", // Desktop: original layout with expand button
                      },
                      alignItems: "center",
                      gap: 2,
                      mb: 0,
                      p: 1,
                      mx: -1,
                      cursor: "pointer",
                      borderRadius: 2,
                      border: "1px solid transparent",
                      transition: "all 0.1s ease-in-out",
                      "&:hover": {
                        bgcolor: (theme) =>
                          `${alpha(theme.palette.divider, 0.02)}`,

                        border: (theme) =>
                          `1px solid ${alpha(theme.palette.divider, 0.05)}`,
                      },
                    }}
                  >
                    {/* Criteria Name (desktop only) */}
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 500,
                        fontSize: "0.95rem",
                        color: "text.secondary",
                        display: { xs: "none", md: "block" },
                      }}
                    >
                      {criteriaKey}
                    </Typography>

                    {/* Linear Progress */}
                    <LinearProgress
                      variant="determinate"
                      value={criteria.score * 20}
                      sx={{
                        height: 20,
                        borderRadius: 3,
                        backgroundColor: (theme) =>
                          `${alpha(theme.palette.divider, 0.06)}`,
                        "& .MuiLinearProgress-bar": {
                          bgcolor: getGradeColor(criteria.grade),
                          borderRadius: 3,
                        },
                      }}
                    />

                    {/* Score */}
                    {isEditing ? (
                      <TextField
                        value={criteria.score}
                        onChange={(e) =>
                          handleScoreChange(
                            criteriaKey,
                            "score",
                            e.target.value,
                          )
                        }
                        variant="outlined"
                        size="small"
                        type="number"
                        inputProps={{
                          min: 0,
                          max: 5,
                          step: 0.1,
                          style: { textAlign: "left", fontWeight: "500" },
                        }}
                        sx={{ width: "70px", ...styles.whiteBg }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          textAlign: "right",
                        }}
                      >
                        {criteria.score}/5
                      </Typography>
                    )}

                    {/* Grade */}
                    {isEditing ? (
                      <TextField
                        value={criteria.grade}
                        onChange={(e) =>
                          handleScoreChange(
                            criteriaKey,
                            "grade",
                            e.target.value.toUpperCase(),
                          )
                        }
                        variant="outlined"
                        size="small"
                        inputProps={{
                          style: { textAlign: "center", fontWeight: "500" },
                          maxLength: 1,
                        }}
                        sx={{ width: "50px", ...styles.whiteBg }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    ) : (
                      <Box
                        sx={{
                          bgcolor: getGradeColor(criteria.grade),
                          color: "white",
                          px: 1.5,
                          py: 0.5,
                          borderRadius: "6px",
                          textAlign: "center",
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: "500" }}>
                          {criteria.grade}
                        </Typography>
                      </Box>
                    )}

                    {/* Expand/Collapse Button */}
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleMotivation(criteriaKey);
                      }}
                      sx={{
                        p: 0.5,
                        transition: "transform 0.2s ease-in-out",
                        "&:hover": {
                          transform: "scale(1.1)",
                        },
                      }}
                    >
                      {expandedMotivations[criteriaKey] ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Box>

                  {/* Motivation */}
                  <Collapse in={expandedMotivations[criteriaKey]}>
                    <Box
                      sx={{
                        mb: 1,
                        mt: 0.5,
                        p: 1.5,
                        bgcolor: (theme) =>
                          `${alpha(theme.palette.divider, 0.02)}`,
                        borderRadius: 1,
                        borderLeft: `4px solid ${getGradeColor(criteria.grade)}`,
                      }}
                    >
                      {isEditing ? (
                        <TextField
                          value={criteria.motivation || ""}
                          onChange={(e) =>
                            handleScoreChange(
                              criteriaKey,
                              "motivation",
                              e.target.value,
                            )
                          }
                          variant="outlined"
                          size="small"
                          multiline
                          fullWidth
                          placeholder="Skriv motivation..."
                          sx={{ ...styles.whiteBg }}
                        />
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontStyle: criteria.motivation
                              ? "normal"
                              : "italic",
                            lineHeight: "1.6rem",
                            fontSize: "0.9rem",
                          }}
                        >
                          <span style={{ fontWeight: 600 }}> Motivering: </span>
                          {criteria.motivation || "Ingen motivation angiven"}
                        </Typography>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              ),
            )}

            {/* Overall Score Motivation */}
            <Box
              sx={{
                mt: 1,
                pt: 1,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Box
                onClick={() => toggleMotivation("overall")}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",

                  cursor: "pointer",
                  borderRadius: 2,
                  p: 1,
                  mx: -1,
                  border: "1px solid transparent",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    bgcolor: (theme) => `${alpha(theme.palette.divider, 0.02)}`,
                    border: (theme) =>
                      `1px solid ${alpha(theme.palette.divider, 0.05)}`,
                  },
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 500,
                    fontSize: "0.95rem",
                    color: "text.secondary",
                  }}
                >
                  Övergripande motivering
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleMotivation("overall");
                  }}
                  sx={{
                    p: 0.5,
                    transition: "transform 0.2s ease-in-out",
                    "&:hover": {
                      transform: "scale(1.1)",
                    },
                  }}
                >
                  {expandedMotivations["overall"] ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </IconButton>
              </Box>

              <Collapse in={expandedMotivations["overall"]}>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: (theme) => `${alpha(theme.palette.divider, 0.02)}`,
                    mt: 1,
                    borderRadius: 1,
                    borderLeft: `3px solid ${getGradeColor(editedScores.overallScore.grade)}`,
                  }}
                >
                  {isEditing ? (
                    <TextField
                      value={editedScores.overallScore.motivation || ""}
                      onChange={(e) =>
                        handleOverallChange("motivation", e.target.value)
                      }
                      variant="outlined"
                      size="small"
                      multiline
                      rows={3}
                      fullWidth
                      placeholder="Skriv övergripande motivation..."
                      sx={{
                        ...styles.whiteBg,
                      }}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontStyle: editedScores.overallScore.motivation
                          ? "normal"
                          : "italic",
                        lineHeight: "1.6rem",
                        fontSize: "0.9rem",
                      }}
                    >
                      {editedScores.overallScore.motivation ||
                        "Ingen övergripande motivation angiven"}
                    </Typography>
                  )}
                </Box>
              </Collapse>
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default AssessmentDisplay;
